import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { DateField } from "@/components/ui/date-field";
import { Badge } from "@/components/ui/badge";
import { Filter, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export type FilterState = {
  searchTerm: string;
  status: string[];
  paymentMethods: string[];
  clients: string[];
  suppliers: string[];
  categories: string[];
  bankAccounts: string[];
  products: string[];
  valueRange: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  deliveryDateFrom: Date | undefined;
  deliveryDateTo: Date | undefined;
  tipo: string[];
};

type TransactionFiltersProps = {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  clients: Array<{ id: string; nome: string }>;
  suppliers: Array<{ id: string; nome: string }>;
  bankAccounts: Array<{ id: string; nome: string }>;
  paymentMethods: string[];
  paymentMethodNames?: Record<string, string>;
  categories: string[];
  categoryNames?: Record<string, string>;
  statuses: string[];
  products: string[];
  productNames?: Record<string, string>;
  counts?: {
    tipo: Record<"entrada" | "saida", number>;
    status: Record<string, number>;
    paymentMethods: Record<string, number>;
    clients: Record<string, number>;
    suppliers: Record<string, number>;
    categories: Record<string, number>;
    products: Record<string, number>;
    valueRanges: Record<string, number>;
    bankAccounts?: Record<string, number>;
  };
};

type TabType = "resumo" | "status" | "tipo" | "metodos" | "clientes" | "fornecedores" | "categorias" | "contas" | "produtos" | "valores" | "datas" | "previsao";

export function TransactionFilters({
  filters,
  onFiltersChange,
  clients,
  suppliers,
  bankAccounts,
  paymentMethods,
  paymentMethodNames,
  categories,
  categoryNames,
  statuses,
  products,
  productNames,
  counts,
}: TransactionFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");
  const [clientSearch, setClientSearch] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const filterCount =
    (filters.status?.length ?? 0) +
    (filters.paymentMethods?.length ?? 0) +
    (filters.clients?.length ?? 0) +
    (filters.suppliers?.length ?? 0) +
    (filters.categories?.length ?? 0) +
    (filters.bankAccounts?.length ?? 0) +
    (filters.products?.length ?? 0) +
    (filters.tipo?.length ?? 0) +
    (filters.valueRange ? 1 : 0) +
    (filters.dateFrom || filters.dateTo ? 1 : 0) +
    (filters.deliveryDateFrom || filters.deliveryDateTo ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      status: [],
      paymentMethods: [],
      clients: [],
      suppliers: [],
      categories: [],
      bankAccounts: [],
      products: [],
      valueRange: "",
      dateFrom: undefined,
      dateTo: undefined,
      deliveryDateFrom: undefined,
      deliveryDateTo: undefined,
      tipo: [],
    });
  };

  const removeFilter = (type: keyof FilterState, value?: string) => {
    if (type === "status" || type === "paymentMethods" || type === "clients" || type === "suppliers" || type === "categories" || type === "tipo" || type === "bankAccounts" || type === "products") {
      onFiltersChange({
        ...filters,
        [type]: value ? filters[type].filter((v) => v !== value) : [],
      });
    } else if (type === "valueRange") {
      onFiltersChange({ ...filters, valueRange: "" });
    } else if (type === "dateFrom" || type === "dateTo") {
      onFiltersChange({ ...filters, dateFrom: undefined, dateTo: undefined });
    } else if (type === "deliveryDateFrom" || type === "deliveryDateTo") {
      onFiltersChange({ ...filters, deliveryDateFrom: undefined, deliveryDateTo: undefined });
    }
  };

  // Função para normalizar texto removendo acentos
  const normalizeText = (text: string | null | undefined): string => {
    if (!text) return "";
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // Remove acentos
  };

  const filteredClients = clients.filter((c) =>
    normalizeText(c.nome).includes(normalizeText(clientSearch))
  );

  const filteredSuppliers = suppliers.filter((s) =>
    normalizeText(s.nome).includes(normalizeText(supplierSearch))
  );

  const filteredProducts = (products || []).filter((p) => {
    const productName = productNames?.[p] || p;
    return normalizeText(productName).includes(normalizeText(productSearch));
  });

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "tipo", label: "Tipo", icon: "🔄" },
    { id: "status", label: "Status", icon: "📊" },
    { id: "metodos", label: "Métodos", icon: "💳" },
    { id: "clientes", label: "Clientes", icon: "👥" },
    { id: "fornecedores", label: "Fornecedores", icon: "🏢" },
    { id: "categorias", label: "Categorias", icon: "📁" },
    { id: "contas", label: "Contas", icon: "🏦" },
    { id: "produtos", label: "Produtos", icon: "📦" },
    { id: "valores", label: "Valores", icon: "💰" },
    { id: "datas", label: "Datas", icon: "📅" },
    { id: "previsao", label: "Previsão", icon: "🔮" },
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "status": return filters.status.length;
      case "tipo": return filters.tipo.length;
      case "metodos": return filters.paymentMethods.length;
      case "clientes": return filters.clients.length;
      case "fornecedores": return filters.suppliers.length;
      case "categorias": return filters.categories.length;
      case "contas": return filters.bankAccounts?.length ?? 0;
      case "produtos": return filters.products?.length ?? 0;
      case "valores": return filters.valueRange ? 1 : 0;
      case "datas": return (filters.dateFrom || filters.dateTo) ? 1 : 0;
      case "previsao": return (filters.deliveryDateFrom || filters.deliveryDateTo) ? 1 : 0;
      default: return 0;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative">
          <Filter className="h-4 w-4 mr-2" />
          Filtrar
          {filterCount > 0 && (
            <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
              {filterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[700px] p-0" align="start">
        <div className="flex h-[500px]">
          {/* Sidebar */}
          <div className="w-48 border-r bg-muted/30 p-2 overflow-y-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      Limpar tudo
                    </Button>
                  )}
                </div>

                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.tipo.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Tipo</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.tipo.map((t) => (
                            <Badge key={t} variant="secondary" className="gap-1">
                              {t === "entrada" ? "Entrada" : "Saída"}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => removeFilter("tipo", t)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {filters.status.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.status.map((s) => (
                            <Badge key={s} variant="secondary" className="gap-1 capitalize">
                              {s}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => removeFilter("status", s)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {filters.paymentMethods.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Métodos de Pagamento</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.paymentMethods.map((m) => (
                            <Badge key={m} variant="secondary" className="gap-1">
                              {m}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => removeFilter("paymentMethods", m)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {filters.clients.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Clientes</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.clients.map((id) => {
                            const client = clients.find((c) => c.id === id);
                            return (
                              <Badge key={id} variant="secondary" className="gap-1">
                                {client?.nome}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => removeFilter("clients", id)}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {filters.suppliers.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Fornecedores</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.suppliers.map((id) => {
                            const supplier = suppliers.find((s) => s.id === id);
                            return (
                              <Badge key={id} variant="secondary" className="gap-1">
                                {supplier?.nome}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => removeFilter("suppliers", id)}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {filters.categories.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Categorias</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.categories.map((c) => {
                            const categoryName = categoryNames?.[c] || c;
                            return (
                              <Badge key={c} variant="secondary" className="gap-1">
                                {categoryName}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => removeFilter("categories", c)}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {filters.products.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Produtos</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.products.map((productId) => {
                            const productName = productNames?.[productId] || productId;
                            return (
                              <Badge key={productId} variant="secondary" className="gap-1">
                                {productName}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => removeFilter("products", productId)}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {filters.valueRange && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Faixa de Valores</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.valueRange}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("valueRange")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}

                    {(filters.dateFrom || filters.dateTo) && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Data de Lançamento</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.dateFrom && format(filters.dateFrom, "dd/MM/yyyy", { locale: ptBR })}
                            {filters.dateFrom && filters.dateTo && " - "}
                            {filters.dateTo && format(filters.dateTo, "dd/MM/yyyy", { locale: ptBR })}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("dateFrom")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}

                    {(filters.deliveryDateFrom || filters.deliveryDateTo) && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Data de Previsão</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.deliveryDateFrom && format(filters.deliveryDateFrom, "dd/MM/yyyy", { locale: ptBR })}
                            {filters.deliveryDateFrom && filters.deliveryDateTo && " - "}
                            {filters.deliveryDateTo && format(filters.deliveryDateTo, "dd/MM/yyyy", { locale: ptBR })}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeFilter("deliveryDateFrom")}
                            />
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "tipo" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Tipo de Transação</h3>
                <div className="space-y-2">
                  {["entrada", "saida"].map((tipo) => (
                    <div key={tipo} className="flex items-center space-x-2">
                      <Checkbox
                        id={`tipo-${tipo}`}
                        checked={filters.tipo.includes(tipo)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            tipo: checked
                              ? [...filters.tipo, tipo]
                              : filters.tipo.filter((t) => t !== tipo),
                          });
                        }}
                      />
                      <Label htmlFor={`tipo-${tipo}`} className="capitalize flex-1 flex items-center gap-2">
                        {tipo === "entrada" ? "Entrada" : "Saída"}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.tipo?.[tipo as "entrada" | "saida"] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "status" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status</h3>
                <div className="space-y-2">
                  {statuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={filters.status.includes(status)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            status: checked
                              ? [...filters.status, status]
                              : filters.status.filter((s) => s !== status),
                          });
                        }}
                      />
                      <Label htmlFor={`status-${status}`} className="capitalize flex-1 flex items-center gap-2">
                        {status}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.status?.[status] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "metodos" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Métodos de Pagamento</h3>
                <div className="space-y-2">
                  {paymentMethods.map((method) => (
                    <div key={method} className="flex items-center space-x-2">
                      <Checkbox
                        id={`method-${method}`}
                        checked={filters.paymentMethods.includes(method)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            paymentMethods: checked
                              ? [...filters.paymentMethods, method]
                              : filters.paymentMethods.filter((m) => m !== method),
                          });
                        }}
                      />
                      <Label htmlFor={`method-${method}`} className="flex-1 flex items-center gap-2">
                        {paymentMethodNames?.[method] || method}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.paymentMethods?.[method] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "clientes" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Clientes</h3>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar cliente..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <div key={client.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`client-${client.id}`}
                        checked={filters.clients.includes(client.id)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            clients: checked
                              ? [...filters.clients, client.id]
                              : filters.clients.filter((c) => c !== client.id),
                          });
                        }}
                      />
                      <Label htmlFor={`client-${client.id}`} className="flex-1 flex items-center gap-2">
                        {client.nome}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.clients?.[client.id] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "fornecedores" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Fornecedores</h3>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar fornecedor..."
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredSuppliers.map((supplier) => (
                    <div key={supplier.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`supplier-${supplier.id}`}
                        checked={filters.suppliers.includes(supplier.id)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            suppliers: checked
                              ? [...filters.suppliers, supplier.id]
                              : filters.suppliers.filter((s) => s !== supplier.id),
                          });
                        }}
                      />
                      <Label htmlFor={`supplier-${supplier.id}`} className="flex-1 flex items-center gap-2">
                        {supplier.nome}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.suppliers?.[supplier.id] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "categorias" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Categorias</h3>
                <div className="space-y-2">
                  {categories.map((categoryId) => {
                    const categoryName = categoryNames?.[categoryId] || categoryId;
                    return (
                      <div key={categoryId} className="flex items-center space-x-2">
                        <Checkbox
                          id={`category-${categoryId}`}
                          checked={filters.categories.includes(categoryId)}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              categories: checked
                                ? [...filters.categories, categoryId]
                                : filters.categories.filter((c) => c !== categoryId),
                            });
                          }}
                        />
                        <Label htmlFor={`category-${categoryId}`} className="flex-1 flex items-center gap-2">
                          {categoryName}
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.categories?.[categoryId] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === "produtos" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Produtos</h3>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar produto..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredProducts.map((productId) => {
                    const productName = productNames?.[productId] || productId;
                    return (
                      <div key={productId} className="flex items-center space-x-2">
                        <Checkbox
                          id={`product-${productId}`}
                          checked={filters.products.includes(productId)}
                          onCheckedChange={(checked) => {
                            onFiltersChange({
                              ...filters,
                              products: checked
                                ? [...filters.products, productId]
                                : filters.products.filter((p) => p !== productId),
                            });
                          }}
                        />
                        <Label htmlFor={`product-${productId}`} className="flex-1 flex items-center gap-2">
                          {productName}
                          <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                            {counts?.products?.[productId] ?? 0}
                          </Badge>
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === "contas" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Contas Bancárias</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {bankAccounts.map((acc) => (
                    <div key={acc.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`bank-${acc.id}`}
                        checked={filters.bankAccounts.includes(acc.id)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            bankAccounts: checked
                              ? [...filters.bankAccounts, acc.id]
                              : filters.bankAccounts.filter((c) => c !== acc.id),
                          });
                        }}
                      />
                      <Label htmlFor={`bank-${acc.id}`} className="flex-1 flex items-center gap-2">
                        {acc.nome}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.bankAccounts?.[acc.id] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "valores" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Faixa de Valores</h3>
                <RadioGroup value={filters.valueRange} onValueChange={(value) => onFiltersChange({ ...filters, valueRange: value })}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="0-100" id="val1" />
                    <Label htmlFor="val1" className="flex-1 flex items-center gap-2">
                      Até R$ 100
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.valueRanges?.["0-100"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="100-500" id="val2" />
                    <Label htmlFor="val2" className="flex-1 flex items-center gap-2">
                      R$ 100 - R$ 500
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.valueRanges?.["100-500"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="500-1000" id="val3" />
                    <Label htmlFor="val3" className="flex-1 flex items-center gap-2">
                      R$ 500 - R$ 1.000
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.valueRanges?.["500-1000"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1000-5000" id="val4" />
                    <Label htmlFor="val4" className="flex-1 flex items-center gap-2">
                      R$ 1.000 - R$ 5.000
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.valueRanges?.["1000-5000"] ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="5000+" id="val5" />
                    <Label htmlFor="val5" className="flex-1 flex items-center gap-2">
                      Acima de R$ 5.000
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.valueRanges?.["5000+"] ?? 0}</Badge>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {activeTab === "datas" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Data de Lançamento</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Data Inicial</Label>
                    <DateField
                      value={filters.dateFrom ? format(filters.dateFrom, "yyyy-MM-dd") : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        onFiltersChange({
                          ...filters,
                          dateFrom: v ? new Date(v + "T00:00:00") : undefined,
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Data Final</Label>
                    <DateField
                      value={filters.dateTo ? format(filters.dateTo, "yyyy-MM-dd") : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        onFiltersChange({
                          ...filters,
                          dateTo: v ? new Date(v + "T23:59:59") : undefined,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "previsao" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Data de Previsão</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Data Inicial</Label>
                    <DateField
                      value={filters.deliveryDateFrom ? format(filters.deliveryDateFrom, "yyyy-MM-dd") : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        onFiltersChange({
                          ...filters,
                          deliveryDateFrom: v ? new Date(v + "T00:00:00") : undefined,
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Data Final</Label>
                    <DateField
                      value={filters.deliveryDateTo ? format(filters.deliveryDateTo, "yyyy-MM-dd") : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        onFiltersChange({
                          ...filters,
                          deliveryDateTo: v ? new Date(v + "T23:59:59") : undefined,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
